<template>
  <div class="news">
    <!-- 使用公共导航栏组件 -->
    <common-header
      :is-scrolled="isScrolled"
      :current-section="currentSection"
      @search="handleSearch"
      @lang-change="switchLang"
      @section-change="handleSectionChange"
      @scroll-to-section="scrollToSection"
    />

    <!-- 页面banner -->
    <div class="page-banner">
      <div class="banner-content">
        <h1 class="animate-fade-in">{{ $t('news.title') }}</h1>
        <p class="animate-fade-in-delay" v-if="$i18n.locale === 'zh'">{{ $t('news.subtitle') }}</p>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-container">
      <!-- 新闻列表部分 -->
      <div class="news-content">
        <div class="news-list">
          <template v-if="$t('news.list') && $t('news.list').length > 0">
            <div class="news-card" v-for="(item, idx) in $t('news.list')" :key="idx">
              <div class="news-img">
                <img :src="require(`@/assets/news/${idx+1}.jpg`)" :alt="item.title" />
              </div>
              <div class="news-info">
                <h3>{{ item.title }}</h3>
                <p class="news-desc">{{ item.desc }}</p>
                <div class="news-meta">
                  <span class="news-date">{{ item.date }}</span>
                  <router-link :to="item.url" class="news-link">{{ $t('news.readMore') }}</router-link>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 使用 CommonSection 组件显示页脚 -->
    <common-section 
      type="footer" 
      id="contact" 
      :current-section="currentSection"
      @scroll-to-section="handleFooterScroll" 
    />
  </div>
</template>

<script>
import CommonHeader from '@/components/CommonHeader.vue'
import CommonSection from '@/components/CommonSection.vue'

export default {
  name: 'NewsView',
  components: {
    CommonHeader,
    CommonSection
  },
  data() {
    return {
      currentSection: 'news',
      currentLang: 'CN',
      sectionLock: false,
      scrollUnlockTimer: null,
      scrollRAF: null,
      scrollTarget: null,
      isScrolled: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.checkHash();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.scrollUnlockTimer) {
      clearTimeout(this.scrollUnlockTimer);
    }
    if (this.scrollRAF) {
      cancelAnimationFrame(this.scrollRAF);
    }
  },
  methods: {
    handleSearch(searchText) {
      if (searchText.trim()) {
        console.log('Searching for:', searchText);
      }
    },
    switchLang(lang) {
      this.currentLang = lang;
    },
    handleSectionChange(section) {
      this.currentSection = section;
    },
    handleScroll() {
      if (this.scrollRAF) {
        cancelAnimationFrame(this.scrollRAF);
      }
      
      this.scrollRAF = requestAnimationFrame(() => {
        // 判断是否滚动到 page-banner 底部
        const bannerEl = document.querySelector('.page-banner');
        if (bannerEl) {
          const bannerBottom = bannerEl.getBoundingClientRect().bottom;
          this.isScrolled = window.scrollY > bannerBottom;
        }

        // 如果正在滚动到目标section，不更新状态
        if (this.sectionLock || this.scrollTarget) {
          return;
        }

        const sections = ['news', 'media', 'events']; // 根据 NewsView 的实际 sections 调整
        const offset = 192;
        let currentSection = this.currentSection;
        let minDistance = Infinity;

        const sectionRects = sections.map(section => {
          const element = document.getElementById(section);
          return element ? {
            section,
            rect: element.getBoundingClientRect()
          } : null;
        }).filter(Boolean);

        sectionRects.forEach(({ section, rect }) => {
          // 只有当section在视口中时才考虑更新状态
          if (rect.top <= offset && rect.bottom >= offset) {
            const distance = Math.abs(rect.top - offset);
            if (distance < minDistance) {
              minDistance = distance;
              currentSection = section;
            }
          }
        });

        if (currentSection !== this.currentSection) {
          this.currentSection = currentSection;
          if (window.location.hash !== `#${currentSection}`) {
            history.replaceState(null, null, `#${currentSection}`);
          }
        }
      });
    },
    checkHash() {
      const hash = window.location.hash.slice(1);
      if (hash === 'contact') {
        this.currentSection = 'contact';
        this.$nextTick(() => {
          const element = document.getElementById('contact');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    },
    handleFooterScroll(section) {
      if (section === 'contact') {
        const element = document.getElementById('contact');
        if (element) {
          const offset = 192;
          const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
          this.sectionLock = true;
          this.currentSection = 'contact';
          window.scrollTo({ top, behavior: 'smooth' });
          if (window.location.hash !== '#contact') {
            history.replaceState(null, null, '#contact');
          }
          if (this.scrollUnlockTimer) clearTimeout(this.scrollUnlockTimer);
          this.scrollUnlockTimer = setTimeout(() => {
            this.sectionLock = false;
          }, 700);
        }
      }
    },
    scrollToSection(section) {
      const element = document.getElementById(section);
      if (element) {
        const offset = 192;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        
        // 设置滚动目标
        this.scrollTarget = section;
        this.sectionLock = true;
        
        requestAnimationFrame(() => {
          window.scrollTo({ top, behavior: 'smooth' });
          
          if (window.location.hash !== `#${section}`) {
            history.replaceState(null, null, `#${section}`);
          }
          
          // 监听滚动结束
          const checkScrollEnd = () => {
            const currentTop = window.pageYOffset;
            if (Math.abs(currentTop - top) < 5) { // 滚动到目标位置附近
              this.currentSection = section;
              this.scrollTarget = null;
              this.sectionLock = false;
            } else {
              requestAnimationFrame(checkScrollEnd);
            }
          };
          
          // 开始检查滚动结束
          setTimeout(checkScrollEnd, 300);
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.news {
  // 滚动条样式
  ::-webkit-scrollbar {
    width: 8px;
    height: 0;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
      background: #a8a8a8;
    }
  }

  // 页面banner样式
  .page-banner {
    height: 65vh;
    background: url('@/assets/news/banner.jpg');
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-align: center;

    .banner-content {
      h1 {
        font-size: 48px;
        margin-bottom: 15px;
        font-weight: 600;
      }

      p {
        font-size: 24px;
        opacity: 0.9;
      }
    }
  }

  // 内容区域样式
  .content-container {
    width: 100%;
    max-width: 1519px;
    margin: 0 auto;
    padding: 60px 20px;
    box-sizing: border-box;
  }

  // 新闻列表样式
  .news-content {
    .news-list {
      display: flex;
      flex-direction: column;
      gap: 76px;
      margin: 40px 0;

      .news-card {
        display: flex;
        background: #fff;
        border-radius: 18px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.08);
        overflow: hidden;
        transition: box-shadow 0.3s;
        align-items: center;
        min-height: 240px;
        padding: 46px 50px;
        &:hover {
          box-shadow: 0 8px 32px rgba(0,0,0,0.13);
          .news-img img {
            transform: scale(1.08);
          }
        }

        .news-img {
          flex: 0 0 550px;
          height: 390px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f6f6f6;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            transition: transform 0.4s cubic-bezier(0.4,0,0.2,1);
          }
        }

        .news-info {
          flex: 1 1 0;
          padding: 32px 36px;
          display: flex;
          flex-direction: column;
          justify-content: center;

          h3 {
            font-size: 22px;
            color: #222;
            font-weight: 700;
            margin-bottom: 16px;
          }

          .news-desc {
            font-size: 16px;
            color: #666;
            margin-bottom: 18px;
            line-height: 1.7;
          }

          .news-meta {
            display: flex;
            align-items: center;
            gap: 32px;

            .news-date {
              font-size: 15px;
              color: #1d2088;
              font-weight: 600;
            }

            .news-link {
              font-size: 15px;
              color: #1d2088;
              background: #f5f7fa;
              border-radius: 6px;
              padding: 6px 18px;
              text-decoration: none;
              transition: background 0.2s;
              &:hover {
                background: #e6f0ff;
              }
            }
          }
        }
      }
    }
  }

  // 动画相关样式
  .animate-fade-in {
    opacity: 0;
    animation: fadeIn 1s ease forwards;
  }

  .animate-fade-in-delay {
    opacity: 0;
    animation: fadeIn 1s ease 0.3s forwards;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .news {
    .page-banner {
      height: 300px;
      .banner-content {
        h1 {
          font-size: 36px;
        }
        p {
          font-size: 18px;
        }
      }
    }

    .news-content {
      .news-list {
        .news-card {
          flex-direction: column;
          min-height: 0;
          .news-img {
            width: 100%;
            height: 180px;
            flex: none;
          }
          .news-info {
            padding: 18px 12px;
          }
        }
      }
    }
  }
}

@media (max-width: 900px) {
  .news-list {
    gap: 24px;
    .news-card {
      flex-direction: column;
      min-height: 0;
      .news-img {
        width: 100%;
        height: 180px;
        flex: none;
      }
      .news-info {
        padding: 18px 12px;
      }
    }

    .news-card:last-child .news-img {
      height: 220px;
    }
  }
}

.no-data {
  width: 100%;
  text-align: center;
  padding: 40px 0;
  color: #333;
  font-size: 26px;
}
</style> 